import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Ctx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false });
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        description: opts.description || "This action can be undone from the Trash for the next 24 hours.",
        confirmText: opts.confirmText || "Delete",
        cancelText: opts.cancelText || "Cancel",
        tone: opts.tone || "danger", // "danger" | "primary"
      });
    });
  }, []);

  function close(ok) {
    setState((s) => ({ ...s, open: false }));
    resolver.current?.(ok);
    resolver.current = null;
  }

  const confirmBtn = state.tone === "primary"
    ? "bg-amber-500 hover:bg-amber-400 text-[hsl(224_60%_6%)] font-semibold"
    : "bg-red-500 hover:bg-red-500/90 text-white font-semibold";

  return (
    <Ctx.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(v) => { if (!v) close(false); }}>
        <AlertDialogContent className="bg-[hsl(224_45%_9%)] border-border" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">{state.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-300">{state.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel" onClick={() => close(false)}>{state.cancelText}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-ok" onClick={() => close(true)} className={confirmBtn}>
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}

export const useConfirm = () => useContext(Ctx);
